import { useEffect, useMemo, useState, type FC, type ReactNode } from "react";
import { Column } from "primereact/column";
import {
  DataTable,
  type DataTablePageEvent,
  type DataTableStateEvent,
} from "primereact/datatable";
import { Dropdown, type DropdownChangeEvent } from "primereact/dropdown";
import DataTableCard from "@/components/DataTableCard/DataTableCard";
import styles from "@/components/DataTableCard/DataTableCard.module.css";
import ImpressionsBarCell from "@/pages/CampaignOverviewPage/components/ImpressionsBarCell";
import audienceStyles from "@/pages/CampaignOverviewPage/components/AudienceSegmentTable.module.css";
import {
  buildSegmentCsv,
  filterSegmentRows,
  SEGMENT_FILTER_FIELDS,
  sortRowsByField,
  type SortOrder,
} from "@/pages/CampaignOverviewPage/utils/audienceSegmentCsv";
import { rankSegmentsByCtr } from "@/pages/CampaignOverviewPage/utils/topPerformers";
import { TEXT } from "@/constants/text";
import type { PerformanceReportRow } from "@/types/reporting";
import {
  RPP_OPTIONS,
  fmt,
  fmtPct,
  fmtStr,
  integerFormatter,
} from "@/utils/tableFormatters";

const SEGMENT_NAME_BODY = ({
  audience_segment_name: segmentName,
}: PerformanceReportRow): ReactNode => (
  <span title={segmentName}>{fmtStr(segmentName)}</span>
);

const VENDOR_BODY = ({
  audience_vendor_name: vendorName,
}: PerformanceReportRow): ReactNode => (
  <span title={vendorName}>{fmtStr(vendorName)}</span>
);

const CTR_BODY = ({ ctr }: PerformanceReportRow): ReactNode => (
  <span className={audienceStyles.ctr}>{fmtPct(ctr)}</span>
);

interface AudienceSegmentTableProps {
  error: string | null;
  isLoading: boolean;
  rows: PerformanceReportRow[];
}

const TABLE_TEXT = TEXT.campaignOverview.audienceSegment;

// Exclude "All" option to prevent mounting 10K+ rows at once.
const RPP_OPTIONS_PAGINATED = RPP_OPTIONS.filter((opt) => opt.value > 0);

const AudienceSegmentTable: FC<AudienceSegmentTableProps> = ({
  rows,
  isLoading,
  error,
}) => {
  const [first, setFirst] = useState(0);
  const [pageRows, setPageRows] = useState(25);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sort, setSort] = useState<{
    field: keyof PerformanceReportRow;
    order: SortOrder;
  } | null>(null);

  // Default order is ranked by CTR (nulls last) — the order the Top performers
  // tile names its leader from. Clicking a header sorts by that column;
  // removableSort lets a third click return to this ranking.
  const rankedRows = useMemo(() => rankSegmentsByCtr(rows), [rows]);
  const maxImpressions = useMemo(
    () => rows.reduce((max, row) => Math.max(max, row.impressions ?? 0), 0),
    [rows]
  );

  useEffect(() => {
    setFirst(0);
  }, [rows]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGlobalFilter(e.target.value);
    setFirst(0);
  };

  const handlePage = (e: DataTablePageEvent) => {
    setFirst(e.first);
  };

  const renderImpressions = (row: PerformanceReportRow) => (
    <ImpressionsBarCell max={maxImpressions} value={row.impressions} />
  );

  // `sortOrder` comes back empty once removableSort's third click clears the
  // sort, which returns the table (and the export) to the CTR ranking.
  const handleSort = ({ sortField, sortOrder }: DataTableStateEvent) => {
    setSort(
      sortOrder
        ? {
            field: sortField as keyof PerformanceReportRow,
            order: sortOrder > 0 ? 1 : -1,
          }
        : null
    );
  };

  const handleRowsChange = (e: DropdownChangeEvent) => {
    setPageRows(e.value as number);
    setFirst(0);
  };

  const handleExportCsv = () => {
    // Exported in the order on screen: the active column sort, else the
    // default CTR ranking.
    const orderedRows = sort
      ? sortRowsByField(rankedRows, sort.field, sort.order)
      : rankedRows;
    const csv = buildSegmentCsv(filterSegmentRows(orderedRows, globalFilter));

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "audience-segments.csv";
    anchor.click();
    // Defer revoke so the browser has time to initiate the async blob fetch.
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  return (
    <DataTableCard
      csvButtonDisabled={isLoading || Boolean(error) || rows.length === 0}
      csvButtonLabel={TABLE_TEXT.csvButton}
      error={error ? TABLE_TEXT.error : null}
      filterPlaceholder={TABLE_TEXT.filterPlaceholder}
      globalFilter={globalFilter}
      isLoading={isLoading}
      subtitle={TABLE_TEXT.subtitle}
      title={TABLE_TEXT.title}
      onExportCsv={handleExportCsv}
      onFilterChange={handleFilterChange}
    >
      <DataTable
        className={styles.table}
        columnResizeMode="fit"
        currentPageReportTemplate="{first}–{last} of {totalRecords}"
        emptyMessage={
          globalFilter.trim() ? TABLE_TEXT.emptyMessage : TABLE_TEXT.noData
        }
        first={first}
        globalFilter={globalFilter}
        globalFilterFields={[...SEGMENT_FILTER_FIELDS]}
        paginator
        paginatorLeft={
          <div className={styles.rppGroup}>
            <label htmlFor="audience-rpp">{TABLE_TEXT.rowsPerPage}</label>
            <Dropdown
              className={styles.rppDropdown}
              inputId="audience-rpp"
              options={RPP_OPTIONS_PAGINATED}
              pt={{ panel: { className: styles.rppDropdownPanel } }}
              value={pageRows}
              onChange={handleRowsChange}
            />
          </div>
        }
        paginatorTemplate="CurrentPageReport PrevPageLink PageLinks NextPageLink"
        pt={{
          bodyRow: { className: styles.tableRow },
          header: { className: styles.columnHeader },
        }}
        removableSort
        resizableColumns
        rows={pageRows}
        scrollable
        scrollHeight="flex"
        sortField={sort?.field}
        sortMode="single"
        sortOrder={sort?.order}
        tableStyle={{ tableLayout: "fixed" }}
        value={rankedRows}
        onPage={handlePage}
        onSort={handleSort}
      >
        <Column
          body={SEGMENT_NAME_BODY}
          field="audience_segment_name"
          header={TABLE_TEXT.columnSegmentName}
          sortable
          style={{ width: "30%" }}
        />
        <Column
          body={VENDOR_BODY}
          field="audience_vendor_name"
          header={TABLE_TEXT.columnVendor}
          sortable
          style={{ width: "20%" }}
        />
        <Column
          body={renderImpressions}
          field="impressions"
          header={TABLE_TEXT.columnImpressions}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "22%" }}
        />
        <Column
          body={(row: PerformanceReportRow) =>
            fmt(row.clicks, integerFormatter)
          }
          field="clicks"
          header={TABLE_TEXT.columnClicks}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "10%" }}
        />
        <Column
          body={CTR_BODY}
          field="ctr"
          header={TABLE_TEXT.columnCtr}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "9%" }}
        />
        <Column
          body={(row: PerformanceReportRow) => fmtPct(row.video_complete_rate)}
          field="video_complete_rate"
          header={TABLE_TEXT.columnVcr}
          headerStyle={{ textAlign: "right" }}
          sortable
          style={{ textAlign: "right", width: "9%" }}
        />
      </DataTable>
    </DataTableCard>
  );
};

export default AudienceSegmentTable;
